/**
 * Local (per-browser) record of "Get started" having been clicked on the
 * full-screen trial WelcomeSplash modal — a fallback for the authoritative
 * server flag (`workspaces.welcome_splash_seen`, mirrored into
 * `billingStore.welcomeSplashSeen`).
 *
 * Why this exists: the dismiss-welcome PUT can be held up for a long time —
 * during a contact import the backend holds a lock on the workspace row that
 * request updates, so the PUT blocks until the import commits (see
 * billingSaga's dismissWelcome comment). The modal must close the instant the
 * user clicks, before that request even lands, and it must stay closed across
 * a refresh even if the request hasn't resolved yet — which needs something
 * that survives a reload and doesn't depend on the server having caught up.
 *
 * Scoped per workspace (the flag itself is `workspaces.welcome_splash_seen`,
 * not per user), so switching workspaces doesn't hide the splash somewhere it
 * hasn't actually been dismissed.
 *
 * Every access is wrapped in try/catch: localStorage can throw in locked-down
 * or private-browsing contexts. When it does, this fails to "not dismissed
 * locally" / does nothing — the in-memory Redux state (set optimistically by
 * WelcomeSplash's click handler) still closes the modal for the current
 * session either way.
 *
 * A null/undefined workspace id gets the same treatment: it is NOT given a
 * shared "unknown" fallback key. WelcomeSplash can render before
 * `authStore.workspace` has loaded, and a shared key would let one
 * workspace-less session's dismissal hide the splash for a completely
 * unrelated workspace-less session on the same browser. So every function
 * below is a no-op (read returns false, without ever touching storage) when
 * given a nullish workspace id — again, the in-memory Redux state still
 * closes the modal for the current session regardless.
 */

const KEY_PREFIX = "welcome_splash_dismissed_";

const keyFor = (workspaceId: number | string): string => `${KEY_PREFIX}${workspaceId}`;

export const isWelcomeSplashDismissedLocally = (
  workspaceId: number | string | null | undefined
): boolean => {
  if (workspaceId === null || workspaceId === undefined) {
    return false;
  }
  try {
    return window.localStorage.getItem(keyFor(workspaceId)) === "true";
  } catch {
    return false;
  }
};

export const markWelcomeSplashDismissedLocally = (
  workspaceId: number | string | null | undefined
): void => {
  if (workspaceId === null || workspaceId === undefined) {
    return;
  }
  try {
    window.localStorage.setItem(keyFor(workspaceId), "true");
  } catch {
    // Nothing to do: the in-memory Redux state still closes the modal for
    // this session; it just won't survive a refresh.
  }
};

export const clearWelcomeSplashDismissedLocally = (
  workspaceId: number | string | null | undefined
): void => {
  if (workspaceId === null || workspaceId === undefined) {
    return;
  }
  try {
    window.localStorage.removeItem(keyFor(workspaceId));
  } catch {
    // Nothing to clear if we can't reach storage in the first place.
  }
};
